import {
  createStyles,
  Dialog,
  DialogContent,
  DialogContentText,
  IconButton,
  makeStyles,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Quotation, RequestIndicatorTypes } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import {
  BasePageQuotation,
  colors,
  Footer,
  HeaderWithPercentBar,
  LottieAnimation,
} from 'design-system';
import { useCallback, useMemo, useState } from 'react';
import { ArrowLeft } from 'react-feather';
import { connect, ConnectedProps } from 'react-redux';
import { getCustomerName } from 'src/functions/quotationMethods';
import {
  completeInspectionRequest,
  createInspectionAction,
  sendInspectionNotification,
} from '../../../store/inspectionForm/actions';
import { RootState } from '../../../store/rootReducer';

import {
  setStartedInspection,
  setStepperInspection,
} from '../../../store/steppers/actions';

// Icons
import CarHistoryIcon from '../../Icons/CarHistoryIcon';
import CarIcon from '../../Icons/CarInfoIcon';
import InspectionSentIcon from '../../Icons/InspectionSentIcon';

// Network
import { SEND_BROKER_EMAIL } from '../../../store/inspectionForm/requestIdentifiers';

// Components
import { ErrorLayout } from '../components';
import Confirmation from './Confirmation';
import InspectionData from './InspectionData';
import InspectionNotification from './InspectionNotification';
import InspectionPhoto from './InspectionPhotos';

// Helper Functions
import getProgressPercent from '../../../functions/getProgressPercent';

// Animation files
import inspectionSpinner from '../../../lottieFiles/inspectionLoadingAnimation.json';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    pageContainer: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    networkDialogue: {
      padding: theme.spacing(1),
    },
    bottomSuccessIndicator: {
      padding: 18,
      position: 'absolute',
      bottom: 80,
      width: '100%',
    },
    dialogContent: {
      background: 'linear-gradient(0.16deg, #FFFFFF 13.23%, #CEE8FF 105.34%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
    },
    spinnerMessage: {
      textAlign: 'center',
      marginTop: '30px',
    },
    IconButtonGoback: {
      width: 'max-content',
    },
    autoPxBanner: {
      padding: '20px 24px',
      backgroundColor: colors.cloud,
    },
  }),
);

export const stepperTitles = [
  'Datos del auto',
  'Fotos del auto',
  'Reporte enviado',
];

export const stepperIcons = [
  <CarHistoryIcon />,
  <CarIcon />,
  <CarIcon />,
  <InspectionSentIcon />,
];

export const InspectionFormManager = ({
  createInspectionActionMethod,
  completeInspectionRequestAction,
  sendInspectionNotificationAction,
  inspectionCreationStatus,
  networkMessage,
  quotation,
  onGoToEmissionRequested,
  currentStepInspection,
  setStepperInspectionAction,
  setStartedInspectionAction,
  onGoToWelcomeInspection,
  carInvoiceFilename,
}: Props): JSX.Element => {
  const sendBrokerEmailStatus = useNetworkObserver(SEND_BROKER_EMAIL);

  const classes = useStyles();
  const [notifiedBroker, toggleNotifiedBroker] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  /**
   * Navigates to the previous page registered in the form manager or to welcome inspection page.
   */
  const goToBackPage = useCallback(() => {
    if (currentStepInspection === 1) {
      onGoToWelcomeInspection();
    } else {
      setStepperInspectionAction(currentStepInspection - 1);
    }
  }, [
    setStepperInspectionAction,
    currentStepInspection,
    onGoToWelcomeInspection,
  ]);

  /**
   * Form that captures written data from the customer
   */
  const dataForm = useMemo(
    () => (
      <InspectionData
        data-testid="dataForm"
        onSubmit={
          carInvoiceFilename !== ''
            ? async (): Promise<void> => {
                await createInspectionActionMethod();
                await completeInspectionRequestAction();
                setStartedInspectionAction(false);
                setStepperInspectionAction(currentStepInspection + 1);
              }
            : async (): Promise<void> => {
                await createInspectionActionMethod();
                setStepperInspectionAction(currentStepInspection + 1);
              }
        }
      />
    ),
    [
      currentStepInspection,
      setStartedInspectionAction,
      setStepperInspectionAction,
      createInspectionActionMethod,
      completeInspectionRequestAction,
      carInvoiceFilename,
    ],
  );

  /**
   * The form that allows the customer to take both his/her vehicle registry image
   * and all of the required car angle photos. Note: all images are encoded in base64.
   */
  const photoForm = useMemo(
    () => (
      <InspectionPhoto
        data-testid="photoform"
        onSubmit={(): void => {
          setStepperInspectionAction(currentStepInspection + 1);
          setStartedInspectionAction(false);
          completeInspectionRequestAction();
        }}
      />
    ),
    [
      currentStepInspection,
      setStartedInspectionAction,
      setStepperInspectionAction,
      completeInspectionRequestAction,
    ],
  );

  /**
   * A list of the pages to render. Note that it does not include the final page, as it is
   * not taken into account for the stepper labels.
   */

  let pages = [dataForm, photoForm];

  if (carInvoiceFilename !== '') {
    pages = [dataForm];
  }

  /**
   * Memoized methods, to be provided as dependencies of useMemo and useCallback implementations
   */
  const memoizedGoToEmission = useCallback(onGoToEmissionRequested, []);
  const memoizedNotifyBroker = useCallback(() => {
    toggleNotifiedBroker(true);
    sendInspectionNotificationAction({
      emailBroker: quotation.user.email,
      clientName: getCustomerName(quotation),
      inspectionURL: `${process.env.REACT_APP_INSPECTION_URL}${quotation.urlSalt}`,
    });
  }, [quotation, sendInspectionNotificationAction]);

  /** Notification page */
  const inspectionNotificationPage = useMemo(
    () => (
      <InspectionNotification
        data-testid="inspection-notification"
        requesting={sendBrokerEmailStatus === 'requesting'}
        quotationCreator={quotation.user.role}
        onEmissionRequested={memoizedGoToEmission}
      />
    ),
    [sendBrokerEmailStatus, quotation.user.role, memoizedGoToEmission],
  );

  /** Confirmation page */
  const confirmationPage = useMemo(
    () => (
      <Confirmation
        data-testid="confirmation"
        requesting={inspectionCreationStatus === 'requesting'}
        onEmissionRequested={memoizedGoToEmission}
        onBrokerActionRequested={memoizedNotifyBroker}
        quotationCreator={quotation.user.role}
        quotation={quotation}
      />
    ),
    [
      inspectionCreationStatus,
      memoizedGoToEmission,
      memoizedNotifyBroker,
      quotation,
    ],
  );

  const backButton = useMemo(
    () => (
      <IconButton
        data-testid="close-button"
        onClick={goToBackPage}
        className={classes.IconButtonGoback}
      >
        <ArrowLeft />
      </IconButton>
    ),
    [goToBackPage, classes.IconButtonGoback],
  );

  /**
   * Don't render anything if the quotation is "invalid".
   */
  if (quotation.id === -1) {
    return <div />;
  }

  return (
    <>
      {!notifiedBroker && (
        <div className={classes.pageContainer}>
          <BasePageQuotation
            data-testid="content"
            rightActions={null}
            leftActions={
              currentStepInspection > pages.length ? null : backButton
            }
            backgroundColor="white"
            stepperItem={
              <HeaderWithPercentBar
                data-testid="stepper"
                goToBackPage={
                  currentStepInspection > pages.length
                    ? undefined
                    : goToBackPage
                }
                icon={stepperIcons[currentStepInspection - 1]}
                stepperTitle={stepperTitles[currentStepInspection - 1]}
                currentProgress={getProgressPercent(
                  currentStepInspection,
                  stepperTitles.length,
                )}
              />
            }
          >
            {inspectionCreationStatus === 'failure' && (
              <>
                <ErrorLayout
                  data-testid="error-layout"
                  titleMessage="No se pudo crear la inspección"
                />
              </>
            )}
            {inspectionCreationStatus !== 'failure' && (
              <>
                {currentStepInspection > pages.length
                  ? confirmationPage
                  : pages[currentStepInspection - 1]}
              </>
            )}
            <Dialog
              data-testid="network-dialogue"
              fullScreen
              className={classes.networkDialogue}
              open={networkMessage !== ''}
            >
              <DialogContent className={classes.dialogContent}>
                <>
                  <DialogContentText>
                    <Typography style={{ fontSize: '19px', fontWeight: 900 }}>
                      Espera un momento
                    </Typography>
                  </DialogContentText>
                  <LottieAnimation
                    data-testid="lottie-animation"
                    animationJson={inspectionSpinner}
                  />
                  <DialogContentText>
                    <Typography variant="caption">
                      <div className={classes.spinnerMessage}>
                        Estamos <strong>procesando</strong>
                        <br />
                        <strong>la inspección</strong> de tu auto
                        <br />
                        (no cierres esta ventana)
                      </div>
                    </Typography>
                  </DialogContentText>
                </>
              </DialogContent>
            </Dialog>
          </BasePageQuotation>
          {networkMessage === '' && (
            <footer data-testid="footerId">
              <Footer
                IsMobile={isMobile}
                country="Costa Rica"
                linkText="Consulte nuestros términos y condiciones"
                linkHref="https://www.assanet.cr/condiciones-generales/#seguros-generales"
              />
            </footer>
          )}
        </div>
      )}
      {notifiedBroker && inspectionNotificationPage}
    </>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  inspectionCreationStatus: RequestIndicatorTypes;
  networkMessage: string;
  currentStepInspection: number;
  carInvoiceFilename: string;
  hasLicense: boolean;
} => ({
  inspectionCreationStatus: state.inspectionForm.inspectionCreationStatus,
  networkMessage: state.inspectionForm.networkMessage,
  quotation: state.quotation.quotation,
  currentStepInspection: state.stepper.currentStepInspection,
  carInvoiceFilename: state.inspectionForm.carInvoiceFilename,
  hasLicense: state.quotation.hasLicense,
});
const reduxActions = {
  completeInspectionRequestAction: completeInspectionRequest,
  sendInspectionNotificationAction: sendInspectionNotification,
  setStepperInspectionAction: setStepperInspection,
  setStartedInspectionAction: setStartedInspection,
  createInspectionActionMethod: createInspectionAction,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onGoToEmissionRequested: () => void;
  onGoToWelcomeInspection: () => void;
}

export default connector(InspectionFormManager);
